import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/widgets-datagrid';
import * as _ from 'underscore';

import { DataAlertsGrid } from '../../app/content/DataAlertsGrid';
import { IPlaceScope } from '../../app/IPlaceScope';
import { AngularToReactDataGridBridge } from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { FeatureFlags } from '../FeatureFlags';
import { ActionButtonsHelper, IActionsMenuScope, SubplaceWithActionButtonsScope } from '../helpers/ActionButtonsHelper';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { SubplaceCountsHelper, SubplaceEvents } from '../helpers/SubplaceCountsHelper';
import { DataAlertContentType } from '../models/ContentTypes';
import { IDataAlertInfo } from '../models/IDataAlertInfo';
import { IEmptyTemplateModel } from '../models/IEmptyTemplate';
import { ISubplace } from '../models/IPlace';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataAlerts } from '../resources/DataAlerts';
import { ResourceQuery } from '../resources/ResourceQuery';
import { IResult } from '../ServerApi/types/IResponse';
import { ActionHelpers } from '../services/ActionHelpers';
import { ActionPanelService, IActionButton } from '../services/ActionPanelService';
import { AddSelfToAlertsAction } from '../services/AddSelfToAlertsAction';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { ChangeDataAlertOwnerAction } from '../services/ChangeDataAlertOwnerAction';
import { DeleteDataAlertsAction } from '../services/DeleteDataAlertsAction';
import { HelpService } from '../services/HelpService';
import { ManageDataAlertRecipientsDialog } from '../services/ManageDataAlertRecipientsDialog';
import { IQueryUpdater, QueryService } from '../services/QueryService';
import { RemoveSelfFromAlertsAction } from '../services/RemoveSelfFromAlertsAction';
import { ResumeDataAlertsAction } from '../services/ResumeDataAlertsAction';
import { ServerService } from '../services/ServerService';
import { SorterFactory } from '../services/SorterFactory';
import { SubplaceWithDataGridScope } from './ContentSubplaceCtrl';

export interface IDataAlertsScope
extends IPlaceScope,
        IActionsMenuScope<IDataAlertInfo, {}>,
        SubplaceWithActionButtonsScope<IDataAlertInfo, {}>,
        SubplaceWithDataGridScope<IDataAlertInfo> { }

export class DataAlertsCtrl {

  public static $inject = [
    '$scope',
    '$translate',
    '$location',
    'AngularToReactDataGridBridge',
    'BrowserTitleService',
    'DataAlerts',
    'ChangeDataAlertOwnerAction',
    'DeleteDataAlertsAction',
    'RemoveSelfFromAlertsAction',
    'ResumeDataAlertsAction',
    'AddSelfToAlertsAction',
    'ManageDataAlertRecipientsDialog',
    'QueryService',
    'ServerService',
    'HelpService',
    'SorterFactory',
    'subplace'
  ];

  private query: IQueryUpdater;
  private static emptyTemplateModel: IEmptyTemplateModel = { title: 'dataAlertsEmptyMessage' };

  constructor(
    private $scope: IDataAlertsScope,
    private $translate: ng.translate.ITranslateService,
    private $location: ng.ILocationService,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge,
            BrowserTitleService: BrowserTitleService,
    private DataAlerts: DataAlerts,
    private ChangeDataAlertOwnerAction: ChangeDataAlertOwnerAction,
    private DeleteDataAlertsAction: DeleteDataAlertsAction,
    private RemoveSelfFromAlertsAction: RemoveSelfFromAlertsAction,
    private ResumeDataAlertsAction: ResumeDataAlertsAction,
    private AddSelfToAlertsAction: AddSelfToAlertsAction,
    private ManageDataAlertRecipientsDialog: ManageDataAlertRecipientsDialog,
            QueryService: QueryService,
    private ServerService: ServerService,
    private HelpService: HelpService,
            SorterFactory: SorterFactory,
            subplace: ISubplace<IDataAlertInfo>
  ) {
    BrowserTitleService.setTitleWithPlaceEntityNameAndSubplaceName({placeEntityName: $scope.objectName, translatableSubplaceName: 'dataAlerts'});

    const selectedItems = new Set<IDataAlertInfo>((dataAlert: IDataAlertInfo) => dataAlert.id);
    $scope.emptyTemplateModel = DataAlertsCtrl.emptyTemplateModel;
    $scope.selectedItems = selectedItems;
    $scope.sorter = SorterFactory.create(DataAlertContentType.defaultOrder);

    const siteUrls = new SiteUrls({siteUrlName: this.ServerService.sessionSiteUrlName()});

    const actions = this.createActions(selectedItems, siteUrls);
    const getActionTarget = ActionButtonsHelper.getActionTargetFn($scope, selectedItems);

    ActionButtonsHelper.setupScopeForActionsMenuInActionBar({
      scope: $scope,
      isActionAvailable: action => action.isAvailable($scope.selectedItems),
      menuActions: actions,
      getActionTarget,
      translate: this.$translate.instant.bind(this.$translate)
    });

    this.AngularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: this.$scope,
      columns: DataAlertsGrid.columns({
        translate: this.$translate.instant.bind(this.$translate),
        scope: $scope,
        actions: actions,
        getActionTarget: getActionTarget,
        siteUrls: siteUrls,
        server: this.ServerService,
        helpService: this.HelpService,
        onPillClick: (dataAlert: IDataAlertInfo) => this.ManageDataAlertRecipientsDialog.open($scope, dataAlert)
      }),
      emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementFor(DataAlertsCtrl.emptyTemplateModel),
      canShowFilterPanel: DataAlertContentType.canShowFilterPanel
    });

    $scope.dataGridProps.updated = (rows: DataGrid.Row[]) => $scope.$apply(() => {
      $scope.selectedItems.updateItems(_.map(_.compact(rows), row => row.data as IDataAlertInfo));
      SubplaceCountsHelper.updateCountsFromSubplace($scope, subplace, rows.length);
    });

    this.query = QueryService.createQueryUpdaterFromSubplace(
      $scope,
      subplace,
      (resourceQuery: ResourceQuery<IDataAlertInfo>) => {
        this.AngularToReactDataGridBridge.updateReactDataGridRowStore({ scope: this.$scope, sliceable: resourceQuery });

        $scope.sliceable = resourceQuery;
        $scope.sorter.setOrder(resourceQuery.getOrder());
      });

    this.refreshDataAlerts();

    $scope.$on(SubplaceEvents.Refresh, () => {
      this.refreshDataAlerts();
    });
  }

  private refreshDataAlerts() {
    this.query.refresh();
  }

  private userCanManageAlerts(alerts: Set<IDataAlertInfo>) {
    return this.ServerService.isSessionSiteOrServerAdmin()
        || alerts.all(a => a.owner.id === this.ServerService.sessionUserId());
  }

  private isAlertSuspended(alerts: Set<IDataAlertInfo>) {
    if(alerts.size() === 0) {
      return false;
    }
    return (alerts.first().suspendState !== 'NotSuspended');
  }

  private isEditOrResumeEnabled(alerts: Set<IDataAlertInfo>) {
    return alerts.size() !== 1 || !this.userCanManageAlerts(alerts) || this.ServerService.isViewerUser();
  }

  private  executeEditOrResumeAction(alerts: Set<IDataAlertInfo>, context: IActionContext, siteUrls: SiteUrls) {
    const alertObj = alerts.first();
    // cannot use $location.path because that will strip parameters
    // Also see Defect 649311: Menu item "Actions/Edit Alert" does not work
    context.scope.$applyAsync(() => this.$location.url(this.DataAlerts.alertOnVizPath(alertObj, siteUrls)));
  }

  private createActions(selectedItems: Set<IDataAlertInfo>, siteUrls: SiteUrls): IActionButton<IDataAlertInfo, {}>[] {

    const removeSelfAction: IActionButton<IDataAlertInfo, {}> = {
      name: 'removeSelf',
      translate: 'removeSelfFromAlert_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (alerts: Set<IDataAlertInfo>) => !this.DataAlerts.userIsSubscribedToAllAlerts(alerts),
      execute: (alerts: Set<IDataAlertInfo>, context: IActionContext) => {
        this.RemoveSelfFromAlertsAction.removeSelf(alerts, context)
          .then((result: IResult) => {
            ActionHelpers.deselectAllExceptErrors(selectedItems, result, CommonErrorCode.DataAlertNotFound);
            this.refreshDataAlerts();
          });
      }
    };

    const addSelfAction: IActionButton<IDataAlertInfo, {}> = {
      name: 'addSelf',
      translate: 'addSelfToAlert_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (alerts: Set<IDataAlertInfo>) => this.DataAlerts.userIsSubscribedToAnyAlerts(alerts),
      execute: (alerts: Set<IDataAlertInfo>, context: IActionContext) => {
        this.AddSelfToAlertsAction.addSelf(alerts, context)
          .then((result: IResult) => {
            ActionHelpers.deselectAllExceptErrors(selectedItems, result, CommonErrorCode.DataAlertNotFound);
            this.refreshDataAlerts();
          });
      }
    };

    const resumeAction: IActionButton<IDataAlertInfo, {}> = {
      name: 'resume',
      translate: 'resumeDataAlert_action',
      isAvailable: this.isAlertSuspended,
      isDisabled: (alerts: Set<IDataAlertInfo>) => this.isEditOrResumeEnabled(alerts),
      execute: (alerts: Set<IDataAlertInfo>, context: IActionContext) => {
        this.ResumeDataAlertsAction.resumeDataAlerts(alerts, context)
          .then(
            (result: IResult) => {
            this.executeEditOrResumeAction(alerts, context, siteUrls);
            }
          );
      }
    };

    const editAction: IActionButton<IDataAlertInfo, {}> = {
      name: 'edit',
      translate: 'editDataAlert_action',
      isAvailable: (alerts: Set<IDataAlertInfo>) =>
        (FeatureFlags.suspendDataAlerts) ?
        ActionPanelService.anySelected(alerts) && (!this.isAlertSuspended(alerts)) :
        ActionPanelService.anySelected(alerts),
      isDisabled: (alerts: Set<IDataAlertInfo>) => this.isEditOrResumeEnabled(alerts),
      execute: (alerts: Set<IDataAlertInfo>, context: IActionContext) => this.executeEditOrResumeAction(alerts, context, siteUrls)
    };

    const deleteAction: IActionButton<IDataAlertInfo, {}> = {
      name: 'delete',
      translate: 'delete_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (alerts: Set<IDataAlertInfo>) => !this.userCanManageAlerts(alerts),
      execute: (alerts: Set<IDataAlertInfo>, context: IActionContext) => {
        this.DeleteDataAlertsAction.deleteDataAlerts(alerts, context)
          .then((result: IResult) => {
            ActionHelpers.deselectAllExceptErrors(selectedItems, result, CommonErrorCode.DataAlertNotFound);
            this.refreshDataAlerts();
          });
      }
    };

    const changeOwnerAction: IActionButton<IDataAlertInfo, {}> = {
      name: 'changeOwner',
      translate: 'changeOwner_action',
      isAvailable: ActionPanelService.anySelected,
      isDisabled: (alerts: Set<IDataAlertInfo>) => !this.userCanManageAlerts(alerts),
      execute: (alerts: Set<IDataAlertInfo>, context: IActionContext) => {
        this.ChangeDataAlertOwnerAction.confirmChangeOwner(alerts, context)
          .then((result: IResult) => {
            ActionHelpers.deselectAllExceptErrors(selectedItems, result, CommonErrorCode.DataAlertNotFound);
            this.refreshDataAlerts();
          });
      }
    };

    return _.compact([
      FeatureFlags.addSelfToDataAlertEnabled ? addSelfAction : null,
      FeatureFlags.removeSelfFromDataAlertEnabled ? removeSelfAction : null,
      FeatureFlags.suspendDataAlerts ? resumeAction : null,
      editAction,
      changeOwnerAction,
      deleteAction
    ]);
  }

}

Module.instance().controller('DataAlertsCtrl', DataAlertsCtrl);
